/**
 * Connections List View
 *
 * Displays a filterable, searchable list of data connections.
 * Follows the same design patterns as the Search Results candidate list.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import {
  useListConnectors,
  type ConnectorConfiguration,
  type ConnectorType,
} from '../../generated/connectors/connectors';
import { cn } from '../../shared/lib/cn';
import { Badge, Button } from '../ui';
import { formatConnectorActivityTime, getConnectorActivityDate } from './dateUtils';
import {
  CONNECTOR_CATALOG,
  getConnectorLabel,
  getConnectorScopeLabel,
  getConnectorSummary,
} from './connectorCatalog';
import { getDataConnectionCategory, type DataConnectionCategoryFilter } from './connectionCategory';

interface ConnectionsListViewProps {
  onCreateNew: () => void;
  onSelectConnection: (connection: ConnectorConfiguration | null) => void;
  selectedConnectionId: string | null;
  defaultTypeFilter?: string;
  /** URL-driven filter: `?category=warehouse`, `catalog`, `apps`, `documents` */
  initialCategoryFilter?: DataConnectionCategoryFilter;
  className?: string;
}

type StatusFilter = 'all' | 'enabled' | 'disabled';
type TypeFilter = 'all' | ConnectorType;

const CATEGORY_SEGMENTS: Array<{ id: DataConnectionCategoryFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'warehouse', label: 'Warehouse Connections' },
  { id: 'catalog', label: 'Catalog Connections' },
  { id: 'apps', label: 'Apps' },
  { id: 'documents', label: 'Documents' },
];

export function ConnectionsListView({
  onCreateNew,
  onSelectConnection,
  selectedConnectionId,
  defaultTypeFilter,
  initialCategoryFilter,
  className,
}: ConnectionsListViewProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(
    defaultTypeFilter && defaultTypeFilter !== 'all'
      ? (defaultTypeFilter as TypeFilter)
      : 'all',
  );
  const [categoryFilter, setCategoryFilter] = useState<DataConnectionCategoryFilter>(
    () => initialCategoryFilter ?? 'all',
  );
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setCategoryFilter(initialCategoryFilter ?? 'all');
  }, [initialCategoryFilter]);

  const { data: connectorsResponse, isLoading } = useListConnectors(
    {
      ...(statusFilter !== 'all' ? { is_enabled: statusFilter === 'enabled' } : {}),
      ...(typeFilter !== 'all' ? { connector_type: typeFilter } : {}),
    },
    {
      query: {
        staleTime: 15000,
        refetchInterval: 30000,
      },
    }
  );

  const connectors = useMemo(
    () => connectorsResponse?.connectors ?? [],
    [connectorsResponse],
  );

  const filteredConnectors = useMemo(() => {
    const searched = searchQuery
      ? connectors.filter((connector) => {
        const query = searchQuery.toLowerCase();
        return (
          connector.connector_name.toLowerCase().includes(query) ||
          connector.connector_type.toLowerCase().includes(query)
        );
      })
      : connectors;

    const categoryScoped =
      categoryFilter === 'all'
        ? searched
        : searched.filter((c) => getDataConnectionCategory(c.connector_type) === categoryFilter);

    return [...categoryScoped].sort((a, b) => {
      const aUpdated = getConnectorActivityDate(a)?.getTime() ?? 0;
      const bUpdated = getConnectorActivityDate(b)?.getTime() ?? 0;
      return bUpdated - aUpdated;
    });
  }, [connectors, searchQuery, categoryFilter]);

  useEffect(() => {
    if (!defaultTypeFilter) {
      return;
    }
    setTypeFilter(
      defaultTypeFilter !== 'all' ? (defaultTypeFilter as TypeFilter) : 'all',
    );
  }, [defaultTypeFilter]);

  useEffect(() => {
    if (filteredConnectors.length === 0) {
      if (selectedConnectionId !== null) {
        onSelectConnection(null);
      }
      return;
    }

    const selectedExists = filteredConnectors.some(
      (connector) => connector.connector_id === selectedConnectionId
    );

    if (!selectedExists) {
      onSelectConnection(filteredConnectors[0]);
    }
  }, [filteredConnectors, onSelectConnection, selectedConnectionId]);

  const totalCount = connectors.length;
  const filteredCount = filteredConnectors.length;

  return (
    <div className={cn('h-full flex flex-col min-h-0', className)}>
      {/* List Header */}
      <div className="flex-shrink-0 px-4 py-4 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-charcoal dark:text-gray-100">Connections</h2>
              <Badge variant="secondary">{filteredCount}</Badge>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {filteredCount === totalCount
                ? `${totalCount} configured source${totalCount === 1 ? '' : 's'}`
                : `${filteredCount} of ${totalCount} configured sources`}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onCreateNew}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add
          </Button>
        </div>

        <div className="relative mb-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search connections..."
            className="w-full h-9 pl-9 pr-3 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-charcoal dark:text-gray-100 text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-eliza-red/50 focus:border-eliza-red transition-colors"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* <FunnelIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" /> */}
          <select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value as DataConnectionCategoryFilter);
              setTypeFilter('all');
            }}
            className="h-8 px-2 pr-7 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-charcoal dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-eliza-red/50 focus:border-eliza-red transition-colors appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw2IDZMMTEgMSIgc3Ryb2tlPSIjOEIzQTUyIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==')] bg-[length:10px] bg-[position:right_0.5rem_center] bg-no-repeat"
          >
            {CATEGORY_SEGMENTS.map(({ id, label }) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </select>

          {categoryFilter === 'warehouse' && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="h-8 px-2 pr-7 border border-eliza-red/30 dark:border-eliza-red/50 rounded-lg bg-eliza-red/5 text-charcoal dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-eliza-red/50 focus:border-eliza-red transition-colors appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw2IDZMMTEgMSIgc3Ryb2tlPSIjOEIzQTUyIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==')] bg-[length:10px] bg-[position:right_0.5rem_center] bg-no-repeat"
            >
              <option value="all">All Warehouses</option>
              <option value="bigquery">BigQuery</option>
              <option value="snowflake">Snowflake</option>
              <option value="postgres">PostgreSQL</option>
              <option value="erp">ERP</option>
              <option value="redshift">Redshift</option>
            </select>
          )}

          {categoryFilter === 'catalog' && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="h-8 px-2 pr-7 border border-eliza-red/30 dark:border-eliza-red/50 rounded-lg bg-eliza-red/5 text-charcoal dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-eliza-red/50 focus:border-eliza-red transition-colors appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw2IDZMMTEgMSIgc3Ryb2tlPSIjOEIzQTUyIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==')] bg-[length:10px] bg-[position:right_0.5rem_center] bg-no-repeat"
            >
              <option value="all">All Catalogs</option>
              <option value="atlan">Atlan</option>
            </select>
          )}

          {categoryFilter === 'all' && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="h-8 px-2 pr-7 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-charcoal dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-eliza-red/50 focus:border-eliza-red transition-colors appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw2IDZMMTEgMSIgc3Ryb2tlPSIjOEIzQTUyIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==')] bg-[length:10px] bg-[position:right_0.5rem_center] bg-no-repeat"
            >
              <option value="all">All Types</option>
              {Object.keys(CONNECTOR_CATALOG).map((type) => (
                <option key={type} value={type}>
                  {getConnectorLabel(type)}
                </option>
              ))}
            </select>
          )}

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="h-8 px-2 pr-7 border border-gray-200 dark:border-dark-border rounded-lg bg-white dark:bg-dark-surface text-charcoal dark:text-gray-100 text-xs focus:outline-none focus:ring-2 focus:ring-eliza-red/50 focus:border-eliza-red transition-colors appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTEgMUw2IDZMMTEgMSIgc3Ryb2tlPSIjOEIzQTUyIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==')] bg-[length:10px] bg-[position:right_0.5rem_center] bg-no-repeat"
          >
            <option value="all">All Status</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {/* Connections List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center">
              <ArrowPathIcon className="h-6 w-6 animate-spin text-eliza-red" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading connections…</p>
            </div>
          </div>
        ) : filteredConnectors.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center px-6 text-center">
            {connectors.length === 0 &&
              !searchQuery &&
              statusFilter === 'all' &&
              typeFilter === 'all' &&
              categoryFilter === 'all' ? (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 dark:bg-dark-surface-2 mb-4">
                  <LinkIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-base font-medium text-charcoal dark:text-gray-100">No connections yet</h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                  Pick a source type, then save a connection instance with the credentials and config your applets will use.
                </p>
                <Button onClick={onCreateNew} className="mt-4">
                  <PlusIcon className="h-4 w-4" />
                  Create Connection
                </Button>
              </>
            ) : (
              <>
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 dark:bg-dark-surface-2 mb-4">
                  <ExclamationCircleIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="text-base font-medium text-charcoal dark:text-gray-100">No matches found</h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                  Try adjusting your search or filters.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setTypeFilter('all');
                    setCategoryFilter('all');
                  }}
                  className="mt-4"
                >
                  Clear filters
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredConnectors.map((connector) => {
              const isActive = connector.connector_id === selectedConnectionId;
              const summary = getConnectorSummary(
                connector.connector_type,
                connector.sync_config,
              );
              const scopeLabel = getConnectorScopeLabel(connector.sync_config);

              return (
                <button
                  key={connector.id}
                  type="button"
                  onClick={() => onSelectConnection(connector)}
                  className={cn(
                    'w-full rounded-lg border px-4 py-3 text-left transition-[background-color,border-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.995] motion-reduce:transition-none',
                    isActive
                      ? 'border-eliza-red/20 bg-eliza-red/8'
                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-dark-surface-2'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg',
                      isActive ? 'bg-eliza-red/20 text-eliza-red' : 'bg-gray-100 dark:bg-dark-surface-2 text-gray-500 dark:text-gray-400'
                    )}>
                      <LinkIcon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          'text-sm font-medium truncate',
                          isActive ? 'text-eliza-red' : 'text-charcoal dark:text-gray-100'
                        )}>
                          {connector.connector_name}
                        </p>
                        <span
                          className={cn(
                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium flex-shrink-0',
                            !connector.is_enabled
                              ? 'bg-gray-100 dark:bg-dark-surface-2 text-gray-500 dark:text-gray-400'
                              : connector.is_healthy !== false
                              ? 'bg-green-500/10 text-green-500'
                              : 'bg-amber-500/10 text-amber-500'
                          )}
                        >
                          {!connector.is_enabled ? (
                            <ClockIcon className="h-3 w-3" />
                          ) : connector.is_healthy !== false ? (
                            <CheckCircleIcon className="h-3 w-3" />
                          ) : (
                            <ExclamationCircleIcon className="h-3 w-3" />
                          )}
                          {!connector.is_enabled
                            ? 'Inactive'
                            : connector.is_healthy !== false
                            ? 'Active'
                            : 'Reauth Required'}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-surface-2 text-[10px] font-medium">
                          {getConnectorLabel(connector.connector_type)}
                        </span>
                        <span>•</span>
                        <span>{scopeLabel}</span>
                        <span>•</span>
                        <span>
                          {formatConnectorActivityTime(connector)}
                        </span>
                      </div>
                      {summary && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                          {summary}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ConnectionsListView;
